import {
  findAll,
  findById,
  create,
  update,
  remove,
  updateRealEstateUserPassword
} from '@/services/realEstateUsers/realEstateUsersService'
import Vue from 'vue'

const state = {
  realEstateUser: null,
  realEstateUsers: [],
  realEstateUserId: null,
  inspectors: [],
  isRealEstateUserLoading: false,
}

const getters = {
  realEstateUser: (state) => state.realEstateUser,
  realEstateUsers: (state) => state.realEstateUsers,
  realEstateUserId: (state) => state.realEstateUserId,
  inspectors: (state) => state.inspectors,
  isRealEstateUserLoading: (state) => state.isRealEstateUserLoading,
}

const actions = {
  async fetchRealEstateUser({ commit, dispatch }, realEstateUserId) {
    commit('setIsRealEstateUserLoading', true)

    try {
      const { data } = await findById(realEstateUserId)
      commit('setRealEstateUser', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsRealEstateUserLoading', false)
    }
  },

  async fetchRealEstateUsers({ commit, dispatch, rootState }) {
    commit('setIsLoading', true)

    const defaultParams = {
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }

    const params = { ...defaultParams, ...rootState.filters.filters }

    try {
      const { data } = await findAll(params)
      const { result, total } = data

      commit('setRealEstateUsers', result)
      commit('setTotalPages', total)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async fetchInspectors({ commit, dispatch }) {
    commit('setIsLoading', true)

    const defaultParams = {
      size: 50,
      page: 0,
    }

    const params = { ...defaultParams, userType: 'ROLE_INSPECTOR', onChecklist: true }

    try {
      const { data } = await findAll(params)
      const { result, total } = data

      commit('setInspectors', result)
      commit('setTotalPages', total)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async createRealEstateUsers({ commit, dispatch }, realEstateUserData) {
    commit('setIsLoading', true)

    const { realEstateUser, form } = realEstateUserData
    const { payload } = realEstateUser

    await create(payload)
      .then(() => {
        commit('closeAllModals')
        commit('setRealEstateUser', {})
        dispatch('fetchRealEstateUsers')
        dispatch('showSuccessMessage', 'Usuário criado com sucesso!')

        form.reset()
      })
      .catch(({ data }) => {
        if (data.errors) {
          let errorMessage = ''
          data.errors.forEach((error) => {
            errorMessage +=
              Vue.i18n.translate('host.label.' + error.field) +
              ': ' +
              error.message +
              ' '
          })
          dispatch('showErrorMessage', errorMessage)
        } else {
          dispatch('showErrorMessage', data.message)
        }
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async editRealEstateUser({ commit, dispatch }, realEstateUserData) {
    commit('setIsLoading', true)

    const { realEstateUser, form } = realEstateUserData
    const { id, payload } = realEstateUser

    await update(id, payload)
      .then(() => {
        commit('closeAllModals')
        commit('setRealEstateUser', {})
        dispatch('fetchRealEstateUsers')
        dispatch('showSuccessMessage', 'Usuário atualizado com sucesso!')

        form.reset()
      })
      .catch(({ data }) => {
        if (data.errors) {
          let errorMessage = ''
          data.errors.forEach((error) => {
            errorMessage +=
              Vue.i18n.translate('host.label.' + error.field) +
              ': ' +
              error.message
          })
          dispatch('showErrorMessage', errorMessage)
        } else {
          dispatch('showErrorMessage', data.message)
        }
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async deleteRealEstateUser({ commit, dispatch }, realEstateUserId) {
    commit('setIsLoading', true)

    await remove(realEstateUserId)
      .then(() => {
        commit('closeAllModals')
        commit('setRealEstateUser', {})
        dispatch('fetchRealEstateUsers')
        dispatch('showSuccessMessage', 'Usuário removido com sucesso!')
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async handleOpenRealEstateUserModal({ dispatch, commit }, realEstateUserId) {
    commit('setRealEstateUser', null)
    await dispatch('fetchCurrentRealEstate')
    if (realEstateUserId) {
      await dispatch('fetchRealEstateUser', realEstateUserId)
    }
    dispatch('openModal', 'CREATE_EDIT_REAL_ESTATE_USER_MODAL')
  },

  handleOpenRealEstateUserDeleteModal({ dispatch, commit }, realEstateUserId) {
    commit('setRealEstateUser', null)
    dispatch('openModal', 'DELETE_REAL_ESTATE_USER_MODAL')
    commit('setRealEstateUserId', realEstateUserId)
    realEstateUserId && dispatch('fetchRealEstateUser', realEstateUserId)
  },

  async updateRealEstateUserPassword({ commit, dispatch }, body) {
    commit('setIsLoading', true)

    await updateRealEstateUserPassword(body)
      .then(() => {
        dispatch('fetchUser')
        commit('closeAllModals')
        dispatch('showSuccessMessage', 'Senha atualizada com sucesso!')
      })
      .catch(({ data }) => {
        dispatch('showErrorMessage', data.message)
      })
      .finally(() => commit('setIsLoading', false))
  },
}

const mutations = {
  setRealEstateUser: (state, realEstateUser) => {
    state.realEstateUser = realEstateUser
  },

  setRealEstateUsers: (state, realEstateUsers) => {
    state.realEstateUsers = realEstateUsers
  },

  setRealEstateUserId: (state, id) => {
    state.realEstateUserId = id
  },

  setInspectors: (state, inspectors) => {
    state.inspectors = inspectors
  },

  setIsRealEstateUserLoading: (state, status) => {
    state.isRealEstateUserLoading = status
  },
}

export default {
  state,
  mutations,
  actions,
  getters,
}
